export const LOGGING_WINDOW_HOURS = {
  HALFDAY: 12,
  ONE_DAY: 24,
  TWO_DAYS: 48,
} as const;

export type LoggingWindowHours = (typeof LOGGING_WINDOW_HOURS)[keyof typeof LOGGING_WINDOW_HOURS];

export interface ILoggingProfile {
  id: string;
  name: string;
  sportType: string;
  level: "basic" | "advanced" | "simple";
  window: LoggingWindowHours;
  status: "active" | "inactive";
  description?: string | null;
}

export interface ISportLevels {
  sportType: string;
  levels: string[];
}

export interface ILoggingProfilePayload {
  name: string;
  sportType: string;
  level: "basic" | "advanced" | "simple";
  window: LoggingWindowHours;
  status: "active" | "inactive";
  description: string | null | undefined;
}

export interface ILoggingProfileTenant {
  id: string;
  name: string;
}

export interface ILoggingProfileFilters {
  search: string;
  sportType: string | null;
  level: string | null;
  status: "active" | "inactive" | "";
  window?: LoggingWindowHours | null;
}

export enum LoggingServiceEnum {
  VIDSWAP = "vidswap",
  LOGGING_PLATFORM = "logging-platform",
}

export type LoggingRuleService = `${LoggingServiceEnum}`;

export type BreakdownBillingMode = "none" | "usage-based" | "credit-based";

export interface LoggingRule {
  // service is informational only and may be absent if the entity has no logging subdocument yet
  // (see LoggingConfig in openapi/atlit/openapi.yaml)
  service?: LoggingRuleService;
  profileId?: string | null;
}

export type LoggingJobTriggerType = "league" | "team" | "superadmin" | "papi";

export type LoggingJobStatus = "pending" | "submitted" | "in_progress" | "completed" | "failed" | "unknown";

export interface LoggingJob {
  id: string;
  eventId: string;
  profileId: string;
  triggerType: LoggingJobTriggerType;
  triggerSource?: string;
  status: LoggingJobStatus;
  failureReason?: string | null;
  eventName?: string;
  sport?: string;
  loggingLevel?: string;
  loggingWindow?: number;
  submittedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Alias so consumers can use the `I`-prefixed naming convention. */
export type ILoggingJob = LoggingJob;

export interface LoggingJobFilters {
  triggerType?: LoggingJobTriggerType | "";
  sport?: string;
  status?: LoggingJobStatus | "";
  loggingLevel?: string;
  loggingWindow?: number | "";
  search?: string;
}

export type BreakdownRequestOutcome = "pending" | "used" | "ignored_existing_rule" | "ignored_already_logged" | "failed";

export type BreakdownRequestService = `${LoggingServiceEnum}`;

export interface BreakdownRequest {
  id: string;
  eventId: string;
  eventName?: string;
  eventStatusAtReceipt?: string;
  outcome: BreakdownRequestOutcome;
  resolvedLevel?: string;
  resolvedWindow?: LoggingWindowHours;
  service?: BreakdownRequestService;
  requestedType?: string;
  requestedPriority?: number;
  userId?: string | null;
  serviceJobId?: number | null;
  resolvedAt?: string | null;
  ignoredReason?: string | null;
  failureReason?: string | null;
  requestedBy?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

/** Alias so consumers can use the `I`-prefixed naming convention. */
export type IBreakdownRequest = BreakdownRequest;

export interface BreakdownRequestFilters {
  outcome?: BreakdownRequestOutcome | "";
  service?: BreakdownRequestService | "";
  resolvedLevel?: string;
  resolvedWindow?: LoggingWindowHours | "";
  requestedType?: string;
  requestedPriority?: number;
  userId?: string;
}
